import * as Yup from 'yup'
import { IPaymentMethodLookupWithRequisiteType } from '../../Requisites/core/response.types'
import { isWalletOnly } from './isWalletOnly'

const cashBoxIdSchema = Yup.object({
  cashBoxId: Yup.number(),
})

const accountNumberShema = (value: number) => {
  return Yup.object({
    accountNumber: value > 0 ? Yup.string().required('Обязательное поле') : Yup.string(),
  })
}

const cashBoxIdSchemaRequired = Yup.object({
  cashBoxId: Yup.number()
    .typeError('Обязательное поле')
    .required('Обязательное поле')
    .positive('Должно быть > 0'),
})

const norm = (v: any) => String(v ?? '').trim();

export const getAllowedRequisiteTypesIntersection = (
  paymentMethodsRaw: unknown,
  lookup: IPaymentMethodLookupWithRequisiteType[]
) => {
  const selected = (Array.isArray(paymentMethodsRaw) ? paymentMethodsRaw : [])
    .map(norm)
    .filter(Boolean);

  if (selected.length === 0) return [];

  const lists = selected.map((pmType) => {
    const found = lookup.find((x) => norm(x.type) === pmType);
    return found?.requisiteTypes ?? [];
  });

  if (lists.some((l) => l.length === 0)) return [];

  let common = new Set<string>(lists[0].map((rt) => rt.code));

  for (let i = 1; i < lists.length; i++) {
    const codes = new Set<string>(lists[i].map((rt) => rt.code));
    const next = new Set<string>();

    common.forEach((c) => {
      if (codes.has(c)) next.add(c);
    });

    common = next;
    if (common.size === 0) return [];
  }

  const byCode = new Map<string, any>();
  for (const list of lists) {
    for (const rt of list) {
      if (common.has(rt.code) && !byCode.has(rt.code)) byCode.set(rt.code, rt);
    }
  }

  return Array.from(byCode.values());
};

export const buildRequisiteSchema = (
  lookup: IPaymentMethodLookupWithRequisiteType[],
  cashBoxSelectIsAvailableByRole: boolean
) => {
  const base = Yup.object().shape({
    paymentMethods: Yup.array()
      .of(Yup.string().oneOf(lookup.map((x) => x.type), 'Неизвестный платежный метод'))
      .min(1, 'Обязательное поле')
      .required('Обязательное поле'),

    requisiteType: Yup.string()
      .required('Обязательное поле')
      .test(
        'requisite-type-allowed',
        'Для выбранных платежных методов нет доступных типов реквизитов',
        function (value) {
          const { paymentMethods } = this.parent as any;

          // 1) пока справочник не загружен — не валидируем ограничение
          if (!lookup.length) return true;

          const pmList = Array.isArray(paymentMethods) ? paymentMethods : [];

          // 2) НЕТ выбранных paymentMethods -> не показываем "нет типов"
          //    (это ошибка paymentMethods, а не requisiteType)
          if (pmList.length === 0) return true;

          const allowed = getAllowedRequisiteTypesIntersection(pmList, lookup);
          const allowedCodes = allowed.map((x: any) => x.code);

          // 3) paymentMethods выбраны, но пересечение пустое -> это уже реальная ошибка
          if (allowedCodes.length === 0) return false;

          // 4) значение пустое -> пусть отрабатывает required
          if (!value) return true;

          // 5) иначе проверяем что выбранный тип входит в пересечение
          return allowedCodes.includes(value);
        }
      ),

    // accountNumber required если есть A2A RUB
    accountNumber: Yup.string().when('paymentMethods', (pms: any, schema: Yup.StringSchema) => {
      const paymentMethods = Array.isArray(pms) ? (pms as string[]) : [];
      const countA2A = paymentMethods.filter((el) => el === 'A2A RUB').length;
      return countA2A > 0 ? schema.required('Обязательное поле') : schema;
    }),

    cardNumber: Yup.string()
      .nullable()
      .when('paymentMethods', (pms: any, schema) =>
        isWalletOnly(pms, lookup) ? schema : schema.required('Обязательное поле')
      ),

    phone: Yup.string()
      .when('requisiteType', (_rt, schema) => schema.required('Обязательное поле'))
      .when('paymentMethods', (pms: any, schema: Yup.StringSchema) => {
        const paymentMethods = Array.isArray(pms) ? (pms as string[]) : [];

        const needsPhone = paymentMethods.some((pm) => {
          const v = String(pm ?? '').toLowerCase();
          return v.includes('sbp') || v.includes('mobile');
        });

        return needsPhone ? schema.required('Обязательное поле') : schema.nullable();
      }),

    cardholderName: Yup.string()
      .nullable()
      .when('paymentMethods', (pms: any, schema) =>
        isWalletOnly(pms, lookup) ? schema : schema.required('Обязательное поле')
      ),
    paymentLimitByAmount: Yup.number().required('Обязательное поле'),
    paymentLimitByTicketCount: Yup.number().required('Обязательное поле'),
    name: Yup.string().required('Обязательное поле'),

    acquiringBank: Yup.string().nullable().when('paymentMethods', (pms: any, schema) => {
      const paymentMethods = Array.isArray(pms) ? (pms as string[]) : [];

      if (isWalletOnly(paymentMethods, lookup)) return schema;

      const needsPhone = paymentMethods.some((pm) => {
        const v = String(pm ?? '').toLowerCase();
        return v.includes('sbp') || v.includes('mobile');
      });

      return needsPhone ? schema.required('Обязательное поле') : schema.nullable();
    }),

    fastTrackMaxClients: Yup.number()
      .min(0, 'Значение должно быть больше или равно 0')
      .max(999, 'Значение должно быть меньше или равно 999')
      .integer('Значение должно быть целым числом'),
  });

  return base.concat(cashBoxSelectIsAvailableByRole ? cashBoxIdSchemaRequired : cashBoxIdSchema);
};
